import { Badge, Logo } from '@artosera/ui';
import { can, type Sujet } from '@artosera/shared';
import { ChevronsLeft, ChevronsRight, Map } from 'lucide-react';
import { useEffect, useRef, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useLocation } from 'react-router';
import { galerieAModule } from '../../auth/sujet.js';
import { Cadenas } from '../module/LectureSeule.js';
import { ENTREE_GROUPE, entreeEnLectureSeule, entreesVisibles } from './navigation.js';
import type { GalerieSession } from '../../api/types.js';
import styles from './Sidebar.module.css';

export interface SidebarProps {
  galerie: GalerieSession;
  galerieSlug: string;
  sujetGroupe: Sujet | null;
  reduite: boolean;
  onReduireChange: (valeur: boolean) => void;
  /** Rend le composant sans le `<nav>` racine quand il vit dans le tiroir mobile. */
  dansMenuMobile?: boolean;
  /**
   * Les modules à venir de la galerie (paliers verrouillés) : l'entrée « Modules »
   * et sa pastille n'existent que s'il y en a.
   */
  modulesAVenir?: number;
  nonLues?: number;
}

/** Barre latérale Halo (3.2) : les onze entrées, dans l'ordre, plus « Groupe ». */
export function Sidebar({
  galerie,
  galerieSlug,
  sujetGroupe,
  reduite,
  onReduireChange,
  dansMenuMobile = false,
  modulesAVenir = 0,
  nonLues = 0,
}: SidebarProps): JSX.Element {
  const { t } = useTranslation(['commun', 'regie']);

  const entrees = entreesVisibles(galerie);

  // La liste défile quand l'écran est bas : l'entrée courante doit rester
  // visible, sans déplacer la page ni le focus.
  const liste = useRef<HTMLUListElement>(null);
  const { pathname } = useLocation();
  useEffect(() => {
    liste.current
      ?.querySelector('[aria-current="page"]')
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [pathname]);

  // La console de groupe est un module complémentaire (D21) : le rôle de
  // groupe ne suffit pas, il faut encore que le module soit actif.
  const groupeVisible =
    sujetGroupe !== null &&
    can(sujetGroupe, 'group:read') &&
    galerieAModule(galerie, 'console-groupe');

  const classes = [styles.barre, reduite && !dansMenuMobile ? styles.reduite : '']
    .filter(Boolean)
    .join(' ');

  return (
    <nav
      data-ui="sidebar"
      className={classes}
      aria-label={t('regie:coquille.navigationPrincipale')}
    >
      <NavLink to={`/${galerieSlug}`} end className={styles.marque} data-ui="brand">
        <Logo collapsed={reduite && !dansMenuMobile} title={t('commun:produit.nom')} />
      </NavLink>

      <ul ref={liste} className={styles.liste}>
        {entrees.map((entree) => {
          const Icone = entree.icone;
          const cible = entree.chemin ? `/${galerieSlug}/${entree.chemin}` : `/${galerieSlug}`;
          const lectureSeule = entreeEnLectureSeule(galerie, entree);
          const libelle = t(entree.libelleNavigation);
          return (
            <li
              key={entree.cle}
              className={entree.cle === 'reglages' ? styles.administration : undefined}
            >
              <NavLink
                data-ui="nav-link"
                data-nav={entree.cle}
                {...(lectureSeule
                  ? {
                      'data-lecture-seule': 'true',
                      'aria-label': t('regie:module.lectureSeule.entree', { libelle }),
                      title: t('regie:module.lectureSeule.etiquette'),
                    }
                  : {})}
                to={cible}
                end={entree.chemin === ''}
                className={({ isActive }) =>
                  [styles.lien, isActive ? styles.actif : ''].filter(Boolean).join(' ')
                }
              >
                <Icone size={18} strokeWidth={1.5} aria-hidden />
                <span className={styles.libelle}>{libelle}</span>
                {lectureSeule ? (
                  <span className={styles.cadenas}>
                    <Cadenas />
                  </span>
                ) : null}
                {entree.cle === 'boite-reception' && nonLues > 0 ? (
                  <Badge aria-label={t('regie:boiteReception.nonLues', { count: nonLues })}>
                    {nonLues}
                  </Badge>
                ) : null}
              </NavLink>
            </li>
          );
        })}
      </ul>

      {modulesAVenir > 0 ? (
        <div className={styles.modules}>
          <Link
            to={`/${galerieSlug}#carte-des-modules`}
            className={styles.lien}
            data-ui="nav-link"
            data-nav="modules"
            aria-label={`${t('regie:modulesAVenir.navigation.libelle')}, ${t(
              'regie:modulesAVenir.navigation.pastille',
              { count: modulesAVenir },
            )}`}
          >
            <Map size={18} strokeWidth={1.5} aria-hidden />
            <span className={styles.libelle}>{t('regie:modulesAVenir.navigation.libelle')}</span>
            <span className={styles.pastille} aria-hidden="true">
              {modulesAVenir}
            </span>
          </Link>
        </div>
      ) : null}

      {groupeVisible ? (
        <div className={styles.groupe}>
          <NavLink
            to={`/${galerieSlug}/${ENTREE_GROUPE.chemin}`}
            className={({ isActive }) =>
              [styles.lien, isActive ? styles.actif : ''].filter(Boolean).join(' ')
            }
          >
            <ENTREE_GROUPE.icone size={18} strokeWidth={1.5} aria-hidden />
            <span className={styles.libelle}>{t(ENTREE_GROUPE.libelleNavigation)}</span>
          </NavLink>
        </div>
      ) : null}

      {dansMenuMobile ? null : (
        <button
          type="button"
          className={styles.reduireBouton}
          onClick={() => onReduireChange(!reduite)}
          aria-label={t(reduite ? 'regie:coquille.etendreNav' : 'regie:coquille.reduireNav')}
        >
          {reduite ? (
            <ChevronsRight size={18} strokeWidth={1.5} aria-hidden />
          ) : (
            <ChevronsLeft size={18} strokeWidth={1.5} aria-hidden />
          )}
          {reduite ? null : <span>{t('regie:coquille.reduireNav')}</span>}
        </button>
      )}
    </nav>
  );
}
